import { useEffect, useState, useCallback } from "react";
import { Wallet, RefreshCw, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

function formatSyncedAt(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
}

/** "Meta ad spend" card - visible only to emails in ADS_VIEWER_EMAILS
 * (backend-enforced; a 403 here just hides the card for everyone else,
 * including other admins). Shared across the Guardians, Donations and
 * Diwali dashboards. No auto-polling - loads once, then only Refresh or a
 * month-selector change re-fetches. */
export default function MetaSpendCard({ testId = "meta-spend-card" }) {
  const [data, setData] = useState(null);
  const [visible, setVisible] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (month) => {
    try {
      const { data } = await api.get("/admin/meta-spend", { params: month ? { month } : {} });
      setData(data);
    } catch (err) {
      if (err.response?.status === 403) setVisible(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const { data: fresh } = await api.post("/admin/meta-spend/refresh", null, { params: { month: data.month } });
      setData(fresh);
    } catch (err) {
      if (err.response?.status === 403) setVisible(false);
    } finally {
      setRefreshing(false);
    }
  };

  if (!visible || !data) return null;

  return (
    <div className="bg-amtm-surface border border-amtm-border rounded-xl p-4 mb-4" data-testid={testId}>
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <span className="flex items-center gap-2 text-amtm-textMuted text-xs">
          <Wallet className="h-3.5 w-3.5" /> Meta Ad Spend
        </span>
        <div className="flex items-center gap-2">
          <Select value={data.month} onValueChange={load}>
            <SelectTrigger className="h-7 w-28 text-xs" data-testid={`${testId}-month-select`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {data.available_months.map((m) => (
                <SelectItem key={m} value={m} data-testid={`${testId}-month-option-${m}`}>{m}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <button
            type="button"
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid={`${testId}-refresh-button`}
            className="h-7 w-7 flex items-center justify-center rounded-lg border border-amtm-border text-amtm-textMuted hover:text-amtm-teal disabled:opacity-50"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>
      {data.token_expired ? (
        <p className="flex items-center gap-1.5 text-destructive font-semibold" data-testid={`${testId}-token-expired`}>
          <AlertTriangle className="h-4 w-4" /> Meta token expired
        </p>
      ) : (
        <p className="font-display text-2xl text-amtm-text" data-testid={`${testId}-amount`}>
          {data.amount === null ? "No data yet" : `₹${Number(data.amount).toLocaleString("en-IN")}`}
        </p>
      )}
      <p className="text-xs text-amtm-textMuted mt-1" data-testid={`${testId}-synced-at`}>
        {data.synced_at ? `Last synced ${formatSyncedAt(data.synced_at)}` : "Not synced yet"}
      </p>
      {!data.token_expired && data.last_sync_error && (
        <p className="text-xs text-destructive mt-1" data-testid={`${testId}-sync-error`}>
          Last sync issue: {data.last_sync_error}
        </p>
      )}
    </div>
  );
}
